import { ArrowDown, ArrowUp } from "lucide-react";
import { type PointerEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { compareValues, formatValue, humanize } from "../../lib/format";

type Row = Record<string, unknown>;

function isPlainObject(value: unknown): value is Row {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** One level of nesting becomes columns: `spread.move` from `{ spread: { move } }`. */
function flatten(row: Row): Row {
  const flat: Row = {};
  for (const [key, value] of Object.entries(row)) {
    if (isPlainObject(value)) for (const [inner, innerValue] of Object.entries(value)) flat[`${key}.${inner}`] = innerValue;
    else flat[key] = value;
  }
  return flat;
}

const MIN_WIDTH = 64;

/** Sortable, resizable table of records; numbers right-aligned in tabular figures. */
export function DataTable({ rows, caption }: { rows: readonly Row[]; caption?: string }) {
  const { t } = useTranslation();
  const flat = useMemo(() => rows.map(flatten), [rows]);
  const columns = useMemo(() => [...new Set(flat.flatMap((row) => Object.keys(row)))], [flat]);
  const [sort, setSort] = useState<{ column: string; descending: boolean } | null>(null);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const sorted = useMemo(() => {
    if (!sort) return flat;
    const ordered = [...flat].sort((a, b) => compareValues(a[sort.column], b[sort.column]));
    return sort.descending ? ordered.reverse() : ordered;
  }, [flat, sort]);
  const numeric = useMemo(
    () => new Set(columns.filter((column) => flat.some((row) => typeof row[column] === "number") && flat.every((row) => row[column] === null || row[column] === undefined || typeof row[column] === "number"))),
    [columns, flat],
  );

  const startResize = (column: string, event: PointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const header = (event.target as HTMLElement).parentElement;
    const start = event.clientX;
    const startWidth = header?.getBoundingClientRect().width ?? 120;
    const move = (moveEvent: globalThis.PointerEvent) => setWidths((current) => ({ ...current, [column]: Math.max(MIN_WIDTH, startWidth + moveEvent.clientX - start) }));
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
  };

  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{t("report.noRows")}</p>;
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm">
        {caption ? <caption className="sr-only">{caption}</caption> : null}
        <thead className="bg-muted">
          <tr>
            {columns.map((column) => {
              const active = sort?.column === column;
              return (
                <th
                  key={column}
                  scope="col"
                  style={widths[column] ? { width: widths[column], minWidth: widths[column] } : undefined}
                  className={`relative whitespace-nowrap px-3 py-2 font-medium ${numeric.has(column) ? "text-right" : "text-left"}`}
                  aria-sort={active ? (sort.descending ? "descending" : "ascending") : "none"}
                >
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:underline"
                    onClick={() => setSort(active ? { column, descending: !sort.descending } : { column, descending: false })}
                  >
                    {humanize(column)}
                    {active ? sort.descending ? <ArrowDown className="h-3 w-3" aria-hidden /> : <ArrowUp className="h-3 w-3" aria-hidden /> : null}
                  </button>
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-ring/40"
                    onPointerDown={(event) => startResize(column, event)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, index) => (
            <tr key={index} className="border-t border-border">
              {columns.map((column) => (
                <td key={column} className={`px-3 py-2 ${numeric.has(column) ? "text-right tabular-nums" : ""}`}>
                  {formatValue(row[column])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
